import { useState, useEffect, useCallback } from 'react';
import type { VisibilityState, ColumnOrderState } from '@tanstack/react-table';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { useOptionalAuthContext } from '@/contexts/AuthContext';

export type TableDensity = 'compact' | 'comfortable';

export interface TablePreferences {
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  pageSize?: number;
  density?: TableDensity;
  version?: number; // Version for future migration support
}

const STORAGE_PREFIX = 'daycare-table-prefs-';
const ANONYMOUS_SCOPE = 'anon';
const CURRENT_VERSION = 1;

/**
 * The storage key for one table and one user.
 *
 * Staff share a browser profile, so a key without the user id let colleagues
 * overwrite each other's column layout (frontend.md, "Namespace persisted UI
 * preferences by the signed-in user"). Signed-out renders share the `anon` scope.
 */
export function tablePreferencesKey(tableId: string, userId?: string | null): string {
  return `${STORAGE_PREFIX}${userId || ANONYMOUS_SCOPE}.${tableId}`;
}

/** The key used before preferences were scoped by user. Read once as a fallback. */
function legacyKey(tableId: string): string {
  return `${STORAGE_PREFIX}${tableId}`;
}

function parse(stored: string | null): TablePreferences | null {
  if (!stored) return null;
  const prefs = JSON.parse(stored) as TablePreferences;
  if (!prefs.version || prefs.version < CURRENT_VERSION) {
    prefs.version = CURRENT_VERSION;
  }
  return prefs;
}

/**
 * Hook for managing table preferences in localStorage
 *
 * @param tableId - Unique identifier for the table (e.g., 'employee-table', 'children-table')
 * @returns Object with preferences and update functions
 *
 * @example
 * const { preferences, updateColumnVisibility } = useTablePreferences('employee-table');
 *
 * // Preferences are automatically loaded from localStorage
 * // and include: columnVisibility, columnOrder, pageSize, density
 */
export function useTablePreferences(tableId?: string) {
  const { i18n } = useLingui();
  const auth = useOptionalAuthContext();
  const storageKey = tableId ? tablePreferencesKey(tableId, auth?.userId) : null;

  // Load preferences from localStorage
  const loadPreferences = useCallback((): TablePreferences | null => {
    if (!storageKey || !tableId) return null;

    try {
      const own = parse(localStorage.getItem(storageKey));
      if (own) return own;

      // A user's first visit after the key gained the user id: adopt the layout
      // the shared key held, and copy it so the user's scope owns it from now on.
      const legacy = parse(localStorage.getItem(legacyKey(tableId)));
      if (legacy) {
        localStorage.setItem(storageKey, JSON.stringify(legacy));
        return legacy;
      }
    } catch (error) {
      console.error('Failed to load table preferences:', error);
    }

    return null;
  }, [storageKey, tableId]);

  // Save preferences to localStorage
  const savePreferences = useCallback((preferences: TablePreferences, showToast = false) => {
    if (!storageKey) return;

    try {
      // Always save with current version
      const prefsWithVersion = { ...preferences, version: CURRENT_VERSION };
      localStorage.setItem(storageKey, JSON.stringify(prefsWithVersion));

      if (showToast) {
        toast.success(i18n._(msg`Table preferences saved`), {
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Failed to save table preferences:', error);
      toast.error(i18n._(msg`Failed to save table preferences`));
    }
  }, [storageKey, i18n]);

  // Initialize preferences
  const [preferences, setPreferences] = useState<TablePreferences>(() => {
    return loadPreferences() || {};
  });

  // Update column visibility
  const updateColumnVisibility = useCallback((visibility: VisibilityState) => {
    setPreferences(prevPrefs => {
      const newPrefs = { ...prevPrefs, columnVisibility: visibility };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, [savePreferences]);

  // Update column order
  const updateColumnOrder = useCallback((order: ColumnOrderState) => {
    setPreferences(prevPrefs => {
      const newPrefs = { ...prevPrefs, columnOrder: order };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, [savePreferences]);

  // Update page size
  const updatePageSize = useCallback((pageSize: number) => {
    setPreferences(prevPrefs => {
      const newPrefs = { ...prevPrefs, pageSize };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, [savePreferences]);

  // Update row density
  const updateDensity = useCallback((density: TableDensity) => {
    setPreferences(prevPrefs => {
      const newPrefs = { ...prevPrefs, density };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, [savePreferences]);

  // Clear preferences
  const clearPreferences = useCallback(() => {
    if (!storageKey) return;

    try {
      // An empty record, not a removal: a missing key would fall back to the
      // legacy shared key on the next load and bring the old layout back.
      localStorage.setItem(storageKey, JSON.stringify({ version: CURRENT_VERSION }));
      setPreferences({});
      toast.success(i18n._(msg`Table preferences reset to defaults`), {
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to clear table preferences:', error);
      toast.error(i18n._(msg`Failed to reset table preferences`));
    }
  }, [storageKey, i18n]);

  // Clerk resolves the user id asynchronously, so the first render reads the
  // anonymous scope. Re-read whenever the key changes, or the user's own layout
  // would stay invisible until the next reload.
  useEffect(() => {
    setPreferences(loadPreferences() || {});
  }, [loadPreferences]);

  return {
    preferences,
    updateColumnVisibility,
    updateColumnOrder,
    updatePageSize,
    updateDensity,
    clearPreferences,
  };
}
